import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ShieldCheck, Cpu, Network, Server, Package, Headphones, Lock, Zap, Download, ArrowRight, Activity, Globe } from "lucide-react";

const categories = [
  { name: "AI Software", icon: Cpu, desc: "Autonomous automation, knowledge & support AI." },
  { name: "Network Tools", icon: Network, desc: "Management, monitoring & security toolkits." },
  { name: "Computer Software", icon: Server, desc: "Infrastructure and IT management platforms." },
  { name: "Digital Products", icon: Package, desc: "Professional bundles and business toolkits." },
];

const steps = [
  { icon: Package, t: "Choose Product", d: "Pick your digital software licence." },
  { icon: Lock, t: "Secure Checkout", d: "Enter details — no account required." },
  { icon: ShieldCheck, t: "Verified Payment", d: "Backend confirms payment server-side." },
  { icon: Download, t: "Instant Download", d: "Receive your secure download + email." },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden grid-bg">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#070B14]/40 to-[#070B14]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 sm:pt-28 sm:pb-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-up">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-mono bg-blue-500/10 text-blue-400 border border-blue-500/30 mb-6">
                <span className="w-2 h-2 rounded-full bg-emerald-400 pulse-dot" /> Funchal · Madeira · Portugal
              </span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none text-white">
                Enterprise <span className="text-[#2563EB]">AI Software</span> &amp; Network Tools
              </h1>
              <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl">
                Revona Services delivers professional AI, networking and IT software with instant secure
                download — plus 24/7 technical support. No account needed. Buy, pay, download.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/products" className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] btn-glow text-white font-semibold px-6 py-3.5 rounded-full" data-testid="hero-browse-btn">
                  Browse Software <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/services" className="inline-flex items-center gap-2 border border-[#1E2F4D] hover:border-[#2563EB] text-white font-semibold px-6 py-3.5 rounded-full transition-colors" data-testid="hero-services-btn">
                  IT &amp; Network Services
                </Link>
              </div>
              <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
                {[["10", "Software Products"], ["24/7", "IT Support"], ["100%", "Secure Delivery"]].map(([n, l]) => (
                  <div key={l}>
                    <div className="font-display text-2xl font-extrabold text-cyan-400">{n}</div>
                    <div className="text-xs text-slate-400">{l}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative fade-up" style={{ animationDelay: "150ms" }}>
              <div className="tactical-card p-2 overflow-hidden">
                <img src="https://images.unsplash.com/photo-1695668548342-c0c1ad479aee?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" alt="Network server room" className="rounded-lg w-full h-[360px] object-cover" />
              </div>
              <div className="absolute -bottom-5 -left-5 glass border border-[#1E2F4D] rounded-xl px-5 py-4 flex items-center gap-3">
                <ShieldCheck className="w-8 h-8 text-emerald-400" />
                <div>
                  <div className="text-sm font-semibold text-white">Server-side verified</div>
                  <div className="text-xs text-slate-400">Payment confirmed before download</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="mb-12">
          <span className="text-xs font-mono uppercase tracking-widest text-cyan-400">Product Categories</span>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-2">Software built for professionals</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {categories.map((c, i) => (
            <Link key={c.name} to={`/products?category=${encodeURIComponent(c.name)}`}
              className="tactical-card p-6 fade-up" style={{ animationDelay: `${i * 60}ms` }}
              data-testid={`category-${c.name.toLowerCase().replace(/[^a-z]/g, "")}`}>
              <c.icon className="w-8 h-8 text-cyan-400 mb-4" />
              <h3 className="font-display text-lg font-semibold text-white">{c.name}</h3>
              <p className="text-sm text-slate-400 mt-2">{c.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#0A0E1A] border-y border-[#1E2F4D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="mb-12 text-center">
            <span className="text-xs font-mono uppercase tracking-widest text-cyan-400">Simple Purchase Flow</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-2">From product to download in minutes</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <div key={s.t} className="relative tactical-card p-6">
                <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-[#2563EB] flex items-center justify-center text-white font-mono text-sm font-bold">{i + 1}</div>
                <s.icon className="w-7 h-7 text-cyan-400 mb-3" />
                <h3 className="font-display font-semibold text-white">{s.t}</h3>
                <p className="text-sm text-slate-400 mt-1">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <span className="text-xs font-mono uppercase tracking-widest text-cyan-400">Digital Products</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-2">Featured software</h2>
          </div>
          <Link to="/products" className="hidden sm:inline-flex items-center gap-1.5 text-cyan-400 hover:text-white font-semibold text-sm">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {products.slice(0, 6).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* SUPPORT CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="tactical-card p-8 sm:p-12 grid md:grid-cols-2 gap-8 items-center" style={{ background: "#0E1626" }}>
          <div>
            <Headphones className="w-10 h-10 text-cyan-400 mb-4" />
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">24/7 IT &amp; Network Support</h2>
            <p className="text-slate-300 mt-3">Computer, IT, networking troubleshooting and business support from our Madeira specialists — around the clock.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/support" className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] btn-glow text-white font-semibold px-5 py-3 rounded-full" data-testid="cta-support-btn">
                Open a Support Request
              </Link>
              <Link to="/services" className="inline-flex items-center gap-2 border border-[#1E2F4D] hover:border-[#2563EB] text-white font-semibold px-5 py-3 rounded-full transition-colors">
                Explore Services
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[[Activity, "Proactive Monitoring"], [Network, "Network Setup"], [Zap, "Fast Response"], [Globe, "Remote & On-site"]].map(([Ic, t]) => (
              <div key={t} className="bg-[#0A0E1A] border border-[#1E2F4D] rounded-xl p-4">
                <Ic className="w-6 h-6 text-cyan-400 mb-2" />
                <div className="text-sm font-medium text-white">{t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
